import { useNavigate } from 'react-router-dom';
import {
  Plane, FileText, Palmtree, Receipt, QrCode, UserCircle2,
  ShoppingBag, Users, LayoutDashboard, ArrowUpRight, Sparkles,
} from 'lucide-react';
import PremiumIconChip from './PremiumIconChip';

const TEMPLATES = [
  {
    id: 'flight-itinerary',
    icon: Plane,
    kicker: 'Travel',
    title: 'Flight Itinerary Maker',
    body: 'Generate a beautiful multi-segment itinerary with real airline logos, seat maps and PDF export.',
    tone: 'from-cyan-400 to-indigo-600',
    glow: 'rgba(0,240,255,0.55)',
    live: true, free: true,
    href: '/templates/flight-itinerary',
  },
  {
    id: 'holiday-planner',
    icon: Palmtree,
    kicker: 'Travel',
    title: 'Holiday Planner',
    body: 'Day-by-day itineraries with hotels, restaurants and downloadable PDFs. Lovable-style.',
    tone: 'from-amber-400 to-rose-500',
    glow: 'rgba(251,146,60,0.55)',
    live: true, free: true,
    href: '/templates/holiday-planner',
  },
  {
    id: 'pdf-editor',
    icon: FileText,
    kicker: 'Business',
    title: 'PDF Editor',
    body: 'Upload, edit, sign, watermark and merge PDFs — right in the browser. Zero cost.',
    tone: 'from-fuchsia-500 to-purple-600',
    glow: 'rgba(217,70,239,0.55)',
    live: true, free: true,
    href: '/templates/pdf-editor',
  },
  {
    id: 'invoice',
    icon: Receipt,
    kicker: 'Business',
    title: 'Invoice Generator',
    body: 'Create clean, tax-ready invoices in seconds. Auto-numbering, totals and PDF download.',
    tone: 'from-emerald-400 to-teal-500',
    glow: 'rgba(16,185,129,0.55)',
    live: true, free: true,
    href: '/templates/invoice',
  },
  {
    id: 'qr-code',
    icon: QrCode,
    kicker: 'Marketing',
    title: 'QR Code Generator',
    body: 'Stylish QR codes for links, WiFi and vCards. Custom colors, sizes, hi-res download.',
    tone: 'from-sky-400 to-cyan-500',
    glow: 'rgba(56,189,248,0.55)',
    live: true, free: true,
    href: '/templates/qr-code',
  },
  {
    id: 'resume',
    icon: UserCircle2,
    kicker: 'Career',
    title: 'Resume Builder',
    body: 'AI polishes your rough details into a metrics-driven, ATS-friendly resume. PDF export.',
    tone: 'from-rose-500 to-pink-600',
    glow: 'rgba(244,63,94,0.55)',
    live: true, free: true,
    href: '/templates/resume',
  },
  {
    id: 'store',
    icon: ShoppingBag,
    kicker: 'Commerce',
    title: 'E-commerce Store',
    body: 'Stripe checkout, product catalog and cart. Launch a store in minutes.',
    tone: 'from-lime-400 to-green-600',
    glow: 'rgba(163,230,53,0.4)',
    live: false, href: '#',
  },
  {
    id: 'crm',
    icon: Users,
    kicker: 'Sales',
    title: 'Mini CRM',
    body: 'Track leads, deals and pipelines with a Kanban board and email sync.',
    tone: 'from-blue-400 to-indigo-600',
    glow: 'rgba(96,165,250,0.4)',
    live: false, href: '#',
  },
  {
    id: 'dash',
    icon: LayoutDashboard,
    kicker: 'Analytics',
    title: 'Business Dashboard',
    body: 'Beautiful KPI dashboards with charts, filters and export.',
    tone: 'from-violet-500 to-fuchsia-600',
    glow: 'rgba(168,85,247,0.4)',
    live: false, href: '#',
  },
];

export default function TemplatesSection() {
  const navigate = useNavigate();
  const liveCount = TEMPLATES.filter((t) => t.live).length;

  return (
    <section
      id="templates"
      className="relative py-24 md:py-32 text-white overflow-hidden"
      style={{
        background:
          'radial-gradient(1200px 800px at 80% 10%, rgba(0,240,255,0.10) 0%, transparent 55%),' +
          'radial-gradient(1000px 700px at 10% 90%, rgba(255,0,127,0.10) 0%, transparent 60%),' +
          'linear-gradient(180deg, #000 0%, #030303 50%, #000 100%)',
      }}
    >
      <div className="absolute inset-0 hero-grid opacity-15 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14 md:mb-16">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.4em] text-[#FF007F] font-semibold uppercase mb-5 font-mono">
            <Sparkles size={12} />
            {liveCount} Free live tools · 3 more coming
          </div>
          <h2 className="font-display text-4xl md:text-6xl font-black tracking-[-0.03em] leading-[1.02]">
            <span className="text-white">Launch a real product</span>
            <br />
            <span className="italic bg-clip-text text-transparent bg-gradient-to-r from-[#00F0FF] to-[#FF007F]">
              in one click.
            </span>
          </h2>
          <p className="mt-5 text-white/60 text-lg max-w-2xl leading-relaxed">
            Pick a template. Add a prompt. Ship a business.
            All {liveCount} live tools below are 100% free forever &mdash; no signup required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {TEMPLATES.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`template-card-${t.id}`}
                onClick={() => t.live && navigate(t.href)}
                className={`group relative text-left rounded-2xl p-6 glass overflow-hidden transition-all duration-500 hover:-translate-y-1 hover:border-white/25 ${
                  t.live ? 'cursor-pointer' : 'cursor-not-allowed opacity-65'
                }`}
              >
                {/* Corner glow */}
                <div
                  className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${t.tone} opacity-30 blur-3xl group-hover:opacity-60 transition-opacity`}
                />

                <div className="relative flex items-start justify-between mb-5">
                  <PremiumIconChip Icon={Icon} gradient={t.tone} glow={t.glow} size={56} iconSize={24} />
                  <div className="flex items-center gap-2">
                    {t.live ? (
                      <>
                        {t.free && (
                          <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-[#00F0FF]/12 border border-[#00F0FF]/30 text-[#00F0FF] font-mono">
                            Free
                          </span>
                        )}
                        <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-emerald-500/15 border border-emerald-400/30 text-emerald-300 font-mono">
                          ● Live
                        </span>
                      </>
                    ) : (
                      <span className="text-[9px] font-bold uppercase tracking-widest px-2 py-1 rounded-full bg-white/5 border border-white/10 text-white/50 font-mono">
                        Soon
                      </span>
                    )}
                    <ArrowUpRight size={16} className="text-white/40 group-hover:text-white group-hover:rotate-45 transition" />
                  </div>
                </div>

                <div className="relative">
                  <div className="font-mono text-[10px] tracking-[0.3em] font-bold uppercase text-white/50 mb-2">
                    {t.kicker}
                  </div>
                  <div className="font-display text-xl font-black text-white leading-tight tracking-tight">
                    {t.title}
                  </div>
                  <p className="mt-2.5 text-sm text-white/70 leading-relaxed">{t.body}</p>
                </div>

                {t.live && (
                  <div className="mt-5 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#00F0FF]">
                    Try it now
                    <span className="inline-block w-6 h-px bg-[#00F0FF] transition-all duration-500 group-hover:w-10" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
